import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import { outlineCardRecipe } from '../recipes/mobile.recipe';
import { DetailField } from './DetailField';
import { Stack } from './Layout';
import { vehicleFacts, type VehicleRowProps } from './VehicleRow';

export interface VehicleCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'color'>,
    Pick<
      VehicleRowProps,
      'make' | 'model' | 'year' | 'color' | 'odometer' | 'plate'
    > {
  vin?: string | null;
  /**
   * Makes the card a button, which calls this: open the vehicle to change
   * it. Left out for a member who may not, and the card is only read.
   */
  onOpen?: () => void;
}

/**
 * The vehicle on a call once it has one, as the Figma's Entry after add
 * frame draws it (865:32414): VIN, Make, Model, Year, Color, ODO and
 * Plate, each a label over its value, one under another in a hairline
 * outline. Tapped, it opens the vehicle to change it.
 *
 *   <VehicleCard vin="1HGCR2F82TW036562" make="Tesla" model="Model 3"
 *     year={2022} color="Grey" odometer={null} plate={null}
 *     onOpen={() => setEditing(true)} />
 *
 * Blank facts read as a dash, as VehicleRow's do. Until the call has a
 * vehicle the section is the dashed Add vehicle tile instead.
 */
export const VehicleCard = React.forwardRef<HTMLDivElement, VehicleCardProps>(
  function VehicleCard(
    {
      vin,
      make,
      model,
      year,
      color,
      odometer,
      plate,
      onOpen,
      className,
      ...props
    },
    ref
  ) {
    const facts: [string, unknown][] = [
      ['VIN', vin],
      ...vehicleFacts({ make, model, year, color, odometer, plate }),
    ];
    // 12 between facts, as the Figma's list is spaced.
    const body = (
      <Stack gap={3}>
        {facts.map(([label, value]) => (
          <DetailField
            key={label}
            label={label}
            value={value as React.ReactNode}
          />
        ))}
      </Stack>
    );
    if (onOpen)
      return (
        <button
          ref={ref as unknown as React.Ref<HTMLButtonElement>}
          type="button"
          onClick={onOpen}
          className={cn(outlineCardRecipe({ interactive: true }), className)}
          {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          {body}
        </button>
      );
    return (
      <div ref={ref} className={cn(outlineCardRecipe(), className)} {...props}>
        {body}
      </div>
    );
  }
);
